import { CSSProperties, RefObject, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { ScrollPosContext } from './context';
export function useScrollLock<T extends HTMLElement = HTMLDivElement>(target: string, initialAquire = false) {
	const { stack, stackInfo, setTop, registerTarget, unregisterTarget, setPosition, getPosition } = useContext(ScrollPosContext);

	const ref = useRef<T>(null);
	const isActive = useMemo(() => {
		return target === stack[0] && stackInfo.current[target]?.isActive;
	}, [target, stack, stackInfo]);

	const wrapperProps = useMemo<{
		ref: RefObject<T | null>;
		'aria-hidden': boolean | undefined;
		inert: boolean | undefined;
		style: CSSProperties | undefined;
	}>(() => {
		if (isActive) {
			return {
				ref,
				'aria-hidden': undefined,
				inert: undefined,
				style: undefined,
			};
		}

		return {
			ref,
			'aria-hidden': true,
			inert: true,
			style: {
				position: 'fixed',
				overflow: 'auto',
				left: '0',
				top: '0',
				right: '0',
				bottom: '0',
				pointerEvents: 'none',
				scrollbarWidth: 'none',
				msOverflowStyle: 'none',
			},
		};
	}, [isActive]);

	const isMounted = useRef(true);
	useLayoutEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Restore scroll position when becoming inactive - must run BEFORE paint
	useLayoutEffect(() => {
		if (!isActive && ref.current) {
			const pos = getPosition(target);
			ref.current.scrollLeft = pos.x;
			ref.current.scrollTop = pos.y;
		}
	}, [isActive, target, getPosition]);

	useLayoutEffect(() => {
		if (isActive) {
			const pos = getPosition(target);
			window.scrollTo(pos.x, pos.y);
		}
	}, [target, isActive, getPosition]);

	useEffect(() => {
		stackInfo.current[target] = { x: 0, y: 0 };
		registerTarget(target);
		return () => {
			unregisterTarget(target);
		};
	}, [unregisterTarget, target, registerTarget, stackInfo]);

	const lock = useCallback(
		(activate = true) => {
			if (activate) {
				setTop(target);
			} else {
				unregisterTarget(target);
			}
		},
		[setTop, target, unregisterTarget],
	);

	useEffect(() => {
		// Initial activation
		if (initialAquire) {
			lock();
		}
	}, [target, initialAquire, lock]);

	useEffect(() => {
		if (!isActive) return;
		function onUnload() {
			if (!isMounted.current) return;
			setPosition(target, { x: window.scrollX, y: window.scrollY });
		}
		window.addEventListener('beforeunload', onUnload);
		return () => {
			window.removeEventListener('beforeunload', onUnload);
		};
	}, [setPosition, isActive, target]);

	return useMemo(() => ({ isActive, wrapperProps, lock, ref }), [isActive, wrapperProps, lock]);
}
